import { useState } from 'react';
import { Box } from '@chakra-ui/react';
import { Info } from 'lucide-react';
import { VStack, Input, FormControl, Pressable, Text } from 'components/ui';

import { FORM_ACTIONS } from '../../utils/formActions.js';

import { useReferrer } from '../../hooks/useReferrer';
import { AffiliateType } from '../../models/affiliate';

const PERSONAL_EMAIL_PROMPT =
  'We recommend using your personal email rather than your employer email.';

function SignUpInfo({ formData, dispatch, isMobile }) {
  const [touched, setTouched] = useState({});
  const [showPreferredName, setShowPreferredName] = useState(false);
  const referrer = useReferrer();
  const disableEmail = !!referrer?.is_saml;
  const isEmployerReferrer =
    referrer?.affiliate_type === AffiliateType.employer;

  return (
    <VStack gap={'18px'} width="100%">
      <Box
        display="flex"
        flexDirection="column"
        gap={'18px'}
        minWidth="200"
        width="100%"
      >
        <Box display="flex" flexDirection="column" width="100%">
          <FormControl required style={{ display: 'flex' }}>
            <FormControl.Label>Legal first name</FormControl.Label>

            <Input
              autoComplete={'off'}
              maxWidth={'100%'}
              type="text"
              data-testid={'signup-first-name-input'}
              value={formData.firstName}
              onChange={(e) => {
                setTouched({ ...touched, firstName: true });
                dispatch({
                  type: FORM_ACTIONS.UPDATE_FORM_FIELD,
                  fieldName: 'firstName',
                  payload: e.target.value,
                });
              }}
            />
          </FormControl>

          {/* Sits with its field rather than as its own row, so the 18px
              rhythm between field groups is not broken. */}
          {!showPreferredName && (
            <Box
              display="flex"
              justifyContent="flex-end"
              maxWidth={'100%'}
              mt={'7px'}
            >
              <Pressable
                alignItems="center"
                bg="white"
                borderColor="blue.100"
                borderWidth="1px"
                data-testid={'signup-preferred-name-toggle'}
                flexDirection="row"
                gap={'7px'}
                onClick={() => setShowPreferredName(true)}
                pl={'6px'}
                pr={'10px'}
                py={'4px'}
                rounded="pill"
              >
                <Box
                  alignItems="center"
                  bg="blue.100"
                  display="flex"
                  height={'16px'}
                  justifyContent="center"
                  rounded="pill"
                  width={'16px'}
                >
                  <Text
                    as="span"
                    color="secondary.500"
                    fontSize={'14px'}
                    fontWeight="600"
                    lineHeight="1"
                  >
                    +
                  </Text>
                </Box>

                <Text
                  as="span"
                  color="secondary.500"
                  fontSize={'12px'}
                  fontWeight="500"
                  lineHeight="1"
                >
                  Preferred Name
                </Text>
              </Pressable>
            </Box>
          )}
        </Box>

        {showPreferredName && (
          <FormControl style={{ display: 'flex' }}>
            <FormControl.Label>Preferred name</FormControl.Label>
            <Input
              autoComplete={'off'}
              maxWidth={'100%'}
              type="text"
              data-testid={'signup-preferred-name-input'}
              value={formData.preferredName || ''}
              onChange={(e) => {
                dispatch({
                  type: FORM_ACTIONS.UPDATE_FORM_FIELD,
                  fieldName: 'preferredName',
                  payload: e.target.value,
                });
              }}
            />
          </FormControl>
        )}

        <FormControl required style={{ display: 'flex' }}>
          <FormControl.Label>Legal last name</FormControl.Label>

          <Input
            autoComplete={'off'}
            maxWidth={'100%'}
            type="text"
            data-testid={'signup-last-name-input'}
            value={formData.lastName}
            onChange={(e) => {
              setTouched({ ...touched, lastName: true });
              dispatch({
                type: FORM_ACTIONS.UPDATE_FORM_FIELD,
                fieldName: 'lastName',
                payload: e.target.value,
              });
            }}
          />
        </FormControl>

        <FormControl
          invalid={formData.email.length > 0 && formData.email.indexOf('@') < 0}
          required
          style={{ display: 'flex' }}
        >
          <FormControl.Label>Email address</FormControl.Label>

          <Input
            disabled={disableEmail}
            autoComplete={'off'}
            maxWidth={'100%'}
            type="email"
            data-testid={'signup-email-input'}
            value={formData.email}
            onChange={(e) => {
              setTouched({ ...touched, email: true });
              dispatch({
                type: FORM_ACTIONS.UPDATE_FORM_FIELD,
                fieldName: 'email',
                payload: e.target.value,
              });
            }}
          />
          {!!touched['email'] && (
            <FormControl.ErrorMessage mt={'6px'}>
              Must be a valid email
            </FormControl.ErrorMessage>
          )}

          {isEmployerReferrer && (
            <FormControl.HelperText
              alignItems="flex-start"
              alignSelf="stretch"
              bg="blue.50"
              borderColor="blue.100"
              borderWidth="1px"
              display="flex"
              gap="10px"
              mt="10px"
              px="14px"
              py="12px"
              role="note"
              rounded="row"
              w="100%"
            >
              <Box
                as="span"
                color="secondary.200"
                display="flex"
                flexShrink={0}
              >
                <Info aria-hidden="true" size={16} />
              </Box>
              <Text as="span" color="secondary.200" fontSize="14px">
                {PERSONAL_EMAIL_PROMPT}
              </Text>
            </FormControl.HelperText>
          )}
        </FormControl>
      </Box>
    </VStack>
  );
}

export default SignUpInfo;
